import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { inviaPreventivo } from "@/lib/api";
import { PREFILL_EVENT, consumePrefill } from "@/lib/smooth";

const inputCls =
  "w-full rounded-xl border border-xmove-ink/15 bg-white px-4 py-3 text-sm text-xmove-ink placeholder:text-xmove-muted/70 outline-none transition-all focus:border-xmove-orange focus:ring-2 focus:ring-xmove-orange/20";

export const LeadForm = ({ idPrefix = "lead", compact = false }) => {
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    nome: "",
    telefono: "",
    tipo_utente: "",
    auto_budget: "",
    email: "",
    consenso: false,
  });

  const hidden = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      gclid: p.get("gclid") || "",
      utm_source: p.get("utm_source") || "",
      utm_medium: p.get("utm_medium") || "",
      utm_campaign: p.get("utm_campaign") || "",
      pagina: window.location.pathname,
    };
  }, []);

  useEffect(() => {
    // prefill salvato prima di un cambio pagina (es. click "Richiedi questa offerta" da un'altra pagina)
    const saved = consumePrefill();
    if (saved) setForm((f) => ({ ...f, auto_budget: saved }));
    const onPrefill = (e) => {
      consumePrefill(); // consumato da questa pagina: evita residui per la prossima
      setForm((f) => ({ ...f, auto_budget: e.detail }));
    };
    window.addEventListener(PREFILL_EVENT, onPrefill);
    return () => window.removeEventListener(PREFILL_EVENT, onPrefill);
  }, []);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.telefono || !form.tipo_utente || !form.auto_budget) {
      toast.error("Compila tutti i campi obbligatori.");
      return;
    }
    if (!form.consenso) {
      toast.error("Devi accettare l'informativa privacy per proseguire.");
      return;
    }
    setSending(true);
    try {
      await inviaPreventivo({ ...form, email: form.email || null, ...hidden });
      navigate("/grazie/");
    } catch {
      toast.error("Errore di invio. Riprova o scrivici su WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3.5" data-testid={`${idPrefix}-form`}>
      <div className={compact ? "space-y-3.5" : "grid gap-3.5 sm:grid-cols-2"}>
        <input
          value={form.nome}
          onChange={set("nome")}
          placeholder="Nome e Cognome *"
          className={inputCls}
          data-testid={`${idPrefix}-nome-input`}
          autoComplete="name"
        />
        <input
          value={form.telefono}
          onChange={set("telefono")}
          placeholder="Telefono *"
          type="tel"
          inputMode="tel"
          className={inputCls}
          data-testid={`${idPrefix}-telefono-input`}
          autoComplete="tel"
        />
      </div>
      <div className={compact ? "space-y-3.5" : "grid gap-3.5 sm:grid-cols-2"}>
        <select
          value={form.tipo_utente}
          onChange={set("tipo_utente")}
          className={`${inputCls} ${form.tipo_utente ? "" : "text-xmove-muted/70"}`}
          data-testid={`${idPrefix}-tipo-select`}
        >
          <option value="" disabled>Sei… *</option>
          <option value="partita-iva">Partita IVA / Professionista</option>
          <option value="azienda">Azienda (SRL, SPA…)</option>
          <option value="privato">Privato</option>
        </select>
        <input
          value={form.auto_budget}
          onChange={set("auto_budget")}
          placeholder="Auto o budget mensile *"
          className={inputCls}
          data-testid={`${idPrefix}-auto-input`}
        />
      </div>
      <input
        value={form.email}
        onChange={set("email")}
        placeholder="Email (facoltativa)"
        type="email"
        className={inputCls}
        data-testid={`${idPrefix}-email-input`}
        autoComplete="email"
      />
      <label className="flex items-start gap-2.5 text-xs leading-relaxed text-xmove-muted" data-testid={`${idPrefix}-privacy-label`}>
        <input
          type="checkbox"
          checked={form.consenso}
          onChange={set("consenso")}
          data-testid={`${idPrefix}-privacy-checkbox`}
          className="mt-0.5 h-4 w-4 shrink-0 accent-xmove-orange"
        />
        Ho letto l'informativa privacy (Iubenda) e acconsento al trattamento dei dati per essere ricontattato ai fini del preventivo. *
      </label>
      <button
        type="submit"
        disabled={sending}
        data-testid={`${idPrefix}-submit-btn`}
        className="group flex w-full items-center justify-center gap-2 rounded-xl bg-xmove-orange py-4 text-[15px] font-bold text-white shadow-soft transition-all hover:bg-xmove-orangeDark hover:shadow-lift active:scale-[0.99] disabled:opacity-60"
      >
        {sending ? <Loader2 className="animate-spin" size={18} /> : null}
        Ricevi il preventivo gratuito
      </button>
      <p className="flex items-center justify-center gap-1.5 text-[11px] text-xmove-muted">
        <Lock size={11} /> Dati protetti · Nessuno spam · Ti richiamiamo in pochi minuti
      </p>
    </form>
  );
};
